/**
 * The message a handed-over ticket travels with on the channels this page
 * opens itself — WhatsApp and the OS share sheet. A recipient opening "here's
 * a link" knows nothing about what they were given; this says which event,
 * when, in whose timezone, where, and which tickets, and then hands over the
 * link.
 *
 * The mail channel is composed nowhere near here. The deployment sends that
 * message itself, so the page's whole part in a mail is handing the callable
 * the address to send it to — which is why `OpenedChannel` below is the
 * handover vocabulary minus e-mail rather than the whole of it.
 *
 * Composed from the very event/venue snapshot the cards on this page render
 * from (order-access.state.ts's OrderAccessEventContext), and dated through
 * @mhmo91/rx's `formatDateRange` — the same call event-identity.view.ts makes,
 * in the event's own timezone — so what the message says and what the link
 * opens cannot disagree.
 *
 * Plain text is the common denominator: only the WhatsApp rendering adds
 * emphasis markers, since `*…*` renders literally in an OS share sheet's own
 * preview. The link is its own field rather than the body's last line,
 * because `navigator.share` carries a `url` of its own and a body already
 * ending in that link would show the recipient two; WhatsApp, which has no
 * url field, appends it itself.
 */

import { formatDateRange } from "@mhmo91/rx";
import { type Locale, translate } from "@momo/i18n";
import type { Handover } from "@momo/types";
import { Ticket } from "@momo/types";
import {
	catchError,
	defer,
	from,
	map,
	type Observable,
	of,
	throwError,
} from "rxjs";
import type {
	OrderAccessEventContext,
	OrderAccessTicket,
} from "./order-access.state.ts";

export type HandoverMessage = {
	/** `navigator.share`'s own title. */
	readonly subject: string;
	/** Every line but the link: who it's for, which event, when, where, and
	 *  the tickets by type and display id. */
	readonly body: string;
	/** The access link that opens exactly these tickets. */
	readonly url: string;
};

export function composeHandoverMessage(input: {
	/** Which catalog the copy reads from. */
	readonly uiLanguage: Locale;
	/** The region-complete BCP-47 tag `Intl` formats the date through — the
	 *  distinct axis @momo/i18n's translate.ts names; the caller passes its
	 *  theme formatting locale (`this.locale`), as event-identity.view.ts does. */
	readonly formattingLocale: string;
	readonly carrier: OpenedChannel;
	readonly recipient: string;
	readonly event: OrderAccessEventContext;
	readonly tickets: readonly OrderAccessTicket[];
	readonly url: string;
}): HandoverMessage {
	// WhatsApp is the one carrier whose renderer reads markers; everywhere
	// else they would be printed as typed.
	const eventName =
		input.carrier === "whatsapp" ? `*${input.event.name}*` : input.event.name;
	const where = [input.event.venue, input.event.address]
		.filter((line): line is string => (line?.length ?? 0) > 0)
		.join(", ");
	return {
		subject: translate(
			input.uiLanguage,
			input.tickets.length === 1
				? "storefront.orderAccess.handover.message.subject"
				: "storefront.orderAccess.handover.message.subjectPlural",
			{ eventName: input.event.name },
		),
		body: [
			translate(
				input.uiLanguage,
				"storefront.orderAccess.handover.message.greeting",
				{ name: input.recipient },
			),
			translate(
				input.uiLanguage,
				input.tickets.length === 1
					? "storefront.orderAccess.handover.message.lead"
					: "storefront.orderAccess.handover.message.leadPlural",
				{ eventName, count: input.tickets.length },
			),
			"",
			formatDateRange(
				input.event.schedule.start.toDate(),
				input.event.schedule.end.toDate(),
				{
					locale: input.formattingLocale,
					month: "long",
					timeZone: input.event.timezone,
				},
			),
			// An event whose snapshot froze neither venue nor address prints no
			// blank line where the place would have been.
			...(where.length > 0 ? [where] : []),
			"",
			...input.tickets.map((ticket) =>
				translate(
					input.uiLanguage,
					"storefront.orderAccess.handover.message.ticketLine",
					{
						typeName: ticket.typeName,
						displayId: Ticket.ticketDisplayId(ticket.ticketId),
					},
				),
			),
		].join("\n"),
		url: input.url,
	};
}

/**
 * The channels this page opens itself, which is the handover vocabulary minus
 * the one the deployment sends: a mail goes out server-side from the address
 * the buyer typed at the E-Mail exit, so no code here ever holds a recipient
 * address. Derived from `Handover.HandoverChannel` rather than spelled out,
 * so a fourth channel joins this set by declaring itself on the entity and
 * nowhere else.
 */
export type OpenedChannel = Exclude<Handover.HandoverChannel, "email">;

/** What became of the message once the carrier had it. A share sheet the
 *  buyer dismissed is them changing their mind about the channel, never about
 *  the handover — which already stands — so it is a settled outcome the
 *  caller reads rather than a failure; anything the carrier actually refuses
 *  errors on the stream instead, where it can be told apart and reported. */
export type HandoverDelivery = "opened" | "dismissed";

/**
 * Hands the composed message to the carrier the buyer chose. Both branches are
 * built inside `defer`, so the navigation and the share sheet happen on
 * subscribe rather than on the call that composes the stream — an unsubscribed
 * carrier must not have already opened a window.
 *
 * WhatsApp carries no url field of its own, so it appends the link to the
 * body; `navigator.share` takes it as its own field, which is what lets a
 * share target render it as a link rather than as trailing text.
 *
 * WhatsApp opens in a new tab where the browser still grants one — the buyer's
 * ticket page stays where it was — and in this tab where it does not, since
 * the record was written before this ran and the buyer's transient activation
 * may have expired waiting for the link.
 *
 * The share carrier is only ever passed here on a platform that has
 * `navigator.share`; the sheet withholds that exit everywhere else rather than
 * offering an action the browser cannot perform.
 */
export function openHandoverMessage(
	carrier: OpenedChannel,
	message: HandoverMessage,
): Observable<HandoverDelivery> {
	return defer(() => {
		if (carrier === "whatsapp") {
			const href = `https://wa.me/?text=${encodeURIComponent(`${message.body}\n\n${message.url}`)}`;
			if (!window.open(href, "_blank", "noopener")) window.location.href = href;
			return of<HandoverDelivery>("opened");
		}
		return from(
			navigator.share({
				title: message.subject,
				text: message.body,
				url: message.url,
			}),
		).pipe(
			map<void, HandoverDelivery>(() => "opened"),
			catchError((err: unknown) =>
				err instanceof DOMException && err.name === "AbortError"
					? of<HandoverDelivery>("dismissed")
					: throwError(() => err),
			),
		);
	});
}
